import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import type { ClientSurveyAnswer, SurveyQuestion, SurveyTemplateDetail } from '../../types/survey';
import {
  filterAnswersForBatchSave,
  normalizeNumericAnswersForSave,
  getNumericCaptureInputProps,
  getNumericRangeHelperText,
  getOrderedQuestions,
  isAnswerableQuestion,
  isInstructionQuestion,
  isLikelySpinnerStepChange,
  isValidNumericAnswer,
  resolveNumericAnswerFromInput
} from '../../utils/surveyQuestions';

interface SurveyClientRow {
  clientId: number;
  displayName: string;
}

interface MultiClientSurveyModalProps {
  open: boolean;
  template: SurveyTemplateDetail | null;
  clients: SurveyClientRow[];
  initialAnswersByClient: Record<number, Record<number, ClientSurveyAnswer>>;
  saving: boolean;
  error?: string;
  onClose: () => void;
  onSaveBatch: (updates: Array<{ clientId: number; answers: ClientSurveyAnswer[] }>, submit: boolean) => void;
}

const MultiClientSurveyModal: React.FC<MultiClientSurveyModalProps> = ({
  open,
  template,
  clients,
  initialAnswersByClient,
  saving,
  error,
  onClose,
  onSaveBatch
}) => {
  const [answersByClient, setAnswersByClient] = useState<Record<number, Record<number, ClientSurveyAnswer>>>({});
  const [numericDraftByKey, setNumericDraftByKey] = useState<Record<string, string>>({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [localError, setLocalError] = useState('');
  const [applyToAll, setApplyToAll] = useState(false);
  const prevOpenRef = useRef(false);

  React.useEffect(() => {
    const openingNow = open && !prevOpenRef.current;
    if (openingNow) {
      setAnswersByClient(initialAnswersByClient || {});
      setNumericDraftByKey({});
      setQuestionIndex(0);
      setLocalError('');
      setApplyToAll(false);
    }
    prevOpenRef.current = open;
  }, [open, initialAnswersByClient]);

  const questions = useMemo(() => getOrderedQuestions(template?.questions), [template]);
  const currentQuestion = questions[questionIndex] || null;
  const currentIsInstruction = currentQuestion ? isInstructionQuestion(currentQuestion) : false;

  const numericDraftKey = (clientId: number, questionId: number) => `${clientId}-${questionId}`;

  const getNumericDisplayValue = (
    clientId: number,
    questionId: number,
    answer?: ClientSurveyAnswer
  ): string => {
    const key = numericDraftKey(clientId, questionId);
    if (Object.prototype.hasOwnProperty.call(numericDraftByKey, key)) {
      return numericDraftByKey[key];
    }
    if (answer?.numericValue == null) return '';
    return String(answer.numericValue);
  };

  const updateAnswers = (sourceClientId: number, questionId: number, patch: Partial<ClientSurveyAnswer>) => {
    const targetClientIds = applyToAll ? clients.map((client) => client.clientId) : [sourceClientId];
    setAnswersByClient((prev) => {
      const next = { ...prev };
      targetClientIds.forEach((clientId) => {
        next[clientId] = {
          ...(next[clientId] || {}),
          [questionId]: {
            surveyQuestionId: questionId,
            ...((next[clientId] || {})[questionId] || {}),
            ...patch
          }
        };
      });
      return next;
    });
  };

  const syncNumericDrafts = (questionId: number, sourceClientId: number, displayValue: string) => {
    const keysToUpdate = applyToAll
      ? clients.map((client) => numericDraftKey(client.clientId, questionId))
      : [numericDraftKey(sourceClientId, questionId)];

    setNumericDraftByKey((prev) => {
      const next = { ...prev };
      keysToUpdate.forEach((key) => {
        if (displayValue === '') {
          delete next[key];
        } else {
          next[key] = displayValue;
        }
      });
      return next;
    });
  };

  const handleNumericFocus = (
    clientId: number,
    questionId: number,
    answer?: ClientSurveyAnswer
  ) => {
    const display =
      answer?.numericValue != null ? String(answer.numericValue) : '';
    syncNumericDrafts(questionId, clientId, display);
  };

  const handleNumericChange = (
    clientId: number,
    questionId: number,
    question: SurveyQuestion,
    rawValue: string,
    previousValue?: number | null
  ) => {
    const parsed = resolveNumericAnswerFromInput(rawValue, question);
    const shouldClamp =
      parsed.numericValue != null &&
      isLikelySpinnerStepChange(previousValue, parsed.numericValue, question);
    const resolved = shouldClamp
      ? resolveNumericAnswerFromInput(rawValue, question, { clamp: true })
      : parsed;

    syncNumericDrafts(questionId, clientId, shouldClamp ? resolved.displayValue : rawValue);
    updateAnswers(clientId, questionId, { numericValue: resolved.numericValue });
  };

  const clearNumericDrafts = (questionId: number, sourceClientId: number) => {
    const keysToClear = applyToAll
      ? clients.map((client) => numericDraftKey(client.clientId, questionId))
      : [numericDraftKey(sourceClientId, questionId)];

    setNumericDraftByKey((prev) => {
      const next = { ...prev };
      keysToClear.forEach((key) => {
        delete next[key];
      });
      return next;
    });
  };

  const handleNumericBlur = (
    clientId: number,
    questionId: number,
    question: SurveyQuestion,
    rawValue: string
  ) => {
    const resolved = resolveNumericAnswerFromInput(rawValue, question, { clamp: true });
    updateAnswers(clientId, questionId, { numericValue: resolved.numericValue });
    clearNumericDrafts(questionId, clientId);
  };

  const isAnswered = (question: SurveyQuestion, answer?: ClientSurveyAnswer) => {
    if (isInstructionQuestion(question)) return true;
    if (!answer) return false;
    switch (question.questionType) {
      case 'TEXT':
        return Boolean(answer.textValue && String(answer.textValue).trim());
      case 'NUMBER':
      case 'SCALE':
        return isValidNumericAnswer(answer.numericValue, question);
      case 'BOOLEAN':
        return typeof answer.booleanValue === 'boolean';
      case 'SINGLE_CHOICE':
        return typeof answer.selectedOptionId === 'number';
      case 'MULTI_CHOICE':
        return Array.isArray(answer.selectedOptionIds) && answer.selectedOptionIds.length > 0;
      default:
        return false;
    }
  };

  const buildBatchUpdates = () => {
    return clients.map((client) => ({
      clientId: client.clientId,
      answers: filterAnswersForBatchSave(
        normalizeNumericAnswersForSave(
          Object.values(answersByClient[client.clientId] || {}),
          questions
        ),
        questions
      )
    }));
  };

  const saveCurrentQuestion = () => {
    if (!currentQuestion?.id) return;
    if (currentIsInstruction) {
      setLocalError('');
      return;
    }
    const qId = Number(currentQuestion.id);
    const updates = clients
      .map((client) => ({
        clientId: client.clientId,
        answers: answersByClient[client.clientId]?.[qId]
          ? [answersByClient[client.clientId][qId]]
          : []
      }))
      .filter((item) => item.answers.length > 0);

    if (updates.length === 0) {
      setLocalError('Captura al menos una respuesta para guardar esta pregunta.');
      return;
    }
    setLocalError('');
    onSaveBatch(updates, false);
  };

  const submitAll = () => {
    if (!template) return;
    for (const client of clients) {
      for (const question of questions) {
        if (!question.id || !isAnswerableQuestion(question)) continue;
        if (!question.required) continue;
        const answer = answersByClient[client.clientId]?.[Number(question.id)];
        if (!isAnswered(question, answer)) {
          setLocalError(
            `Falta respuesta obligatoria para ${client.displayName} en la pregunta: "${question.questionText}".`
          );
          return;
        }
      }
    }

    setLocalError('');
    onSaveBatch(buildBatchUpdates(), true);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Encuesta rápida por pregunta</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {(error || localError) && <Alert severity="error">{error || localError}</Alert>}
          {!template ? (
            <Alert severity="warning">No hay plantilla activa disponible.</Alert>
          ) : !currentQuestion ? (
            <Alert severity="warning">La plantilla no tiene preguntas.</Alert>
          ) : (
            <>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Pregunta {questionIndex + 1} de {questions.length}
                </Typography>
                <Typography variant="h6" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                  {currentQuestion.questionText}
                  {!currentIsInstruction && currentQuestion.required ? ' *' : ''}
                </Typography>
              </Box>

              {!currentIsInstruction && (
                <FormControlLabel
                  control={<Switch checked={applyToAll} onChange={(e) => setApplyToAll(e.target.checked)} />}
                  label="Aplicar misma respuesta a todos"
                />
              )}

              {!currentIsInstruction && (
                <Stack spacing={1.5}>
                  {clients.map((client) => {
                    const qId = Number(currentQuestion.id);
                    const answer = answersByClient[client.clientId]?.[qId];
                    return (
                      <Grid key={client.clientId} container spacing={1.5} alignItems="center">
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {client.displayName}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} md={9}>
                          {(currentQuestion.questionType === 'NUMBER' ||
                            currentQuestion.questionType === 'SCALE') && (
                            <TextField
                              fullWidth
                              type="number"
                              inputProps={getNumericCaptureInputProps(currentQuestion)}
                              helperText={getNumericRangeHelperText(currentQuestion)}
                              value={getNumericDisplayValue(client.clientId, qId, answer)}
                              onFocus={() => handleNumericFocus(client.clientId, qId, answer)}
                              onChange={(e) =>
                                handleNumericChange(
                                  client.clientId,
                                  qId,
                                  currentQuestion,
                                  e.target.value,
                                  answer?.numericValue
                                )
                              }
                              onBlur={(e) =>
                                handleNumericBlur(client.clientId, qId, currentQuestion, e.target.value)
                              }
                            />
                          )}

                          {currentQuestion.questionType === 'TEXT' && (
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              value={answer?.textValue ?? ''}
                              onChange={(e) =>
                                updateAnswers(client.clientId, qId, { textValue: e.target.value })
                              }
                            />
                          )}

                          {currentQuestion.questionType === 'BOOLEAN' && (
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={Boolean(answer?.booleanValue)}
                                  onChange={(e) =>
                                    updateAnswers(client.clientId, qId, { booleanValue: e.target.checked })
                                  }
                                />
                              }
                              label={answer?.booleanValue ? 'Sí' : 'No'}
                            />
                          )}

                          {currentQuestion.questionType === 'SINGLE_CHOICE' && (
                            <FormControl fullWidth>
                              <Select
                                value={answer?.selectedOptionId ?? ''}
                                onChange={(e) =>
                                  updateAnswers(client.clientId, qId, {
                                    selectedOptionId: e.target.value === '' ? null : Number(e.target.value)
                                  })
                                }
                              >
                                <MenuItem value="">Selecciona una opción</MenuItem>
                                {currentQuestion.options.map((option) => (
                                  <MenuItem key={option.id || option.sortOrder} value={option.id}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}

                          {currentQuestion.questionType === 'MULTI_CHOICE' && (
                            <FormControl fullWidth>
                              <Select
                                multiple
                                value={answer?.selectedOptionIds || []}
                                onChange={(e) =>
                                  updateAnswers(client.clientId, qId, {
                                    selectedOptionIds: e.target.value as number[]
                                  })
                                }
                              >
                                {currentQuestion.options.map((option) => (
                                  <MenuItem key={option.id || option.sortOrder} value={Number(option.id)}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Grid>
                      </Grid>
                    );
                  })}
                </Stack>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cerrar</Button>
        <Button
          onClick={() => {
            setNumericDraftByKey({});
            setQuestionIndex((idx) => Math.max(0, idx - 1));
          }}
          disabled={questionIndex <= 0}
        >
          Anterior
        </Button>
        <Button
          onClick={() => {
            setNumericDraftByKey({});
            setQuestionIndex((idx) => Math.min(questions.length - 1, idx + 1));
          }}
          disabled={questionIndex >= questions.length - 1}
        >
          Siguiente
        </Button>
        <Button
          variant="outlined"
          disabled={!currentQuestion || saving || currentIsInstruction}
          onClick={saveCurrentQuestion}
        >
          Guardar pregunta
        </Button>
        <Button variant="contained" disabled={!template || saving} onClick={submitAll}>
          Enviar todas
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultiClientSurveyModal;
